import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import { authApi } from '@/api/authApi'
import { tokenStorage } from '@/api/index'
import type { ClientProfile, LoginPayload } from '@/types/auth'

export const useAuthStore = defineStore('auth', () => {
  const client = ref<ClientProfile | null>(null)
  const bootstrapping = ref(false)
  const bootstrapped = ref(false)

  const isAuthenticated = computed(() => client.value !== null)

  /** При старте app — если есть токены, восстановить сессию. Идемпотентно. */
  async function bootstrap(): Promise<void> {
    if (bootstrapped.value) return
    bootstrapping.value = true
    try {
      if (tokenStorage.hasTokens()) {
        client.value = await authApi.me()
      }
    } catch {
      // Токен невалиден — выходим
      tokenStorage.clear()
      client.value = null
    } finally {
      bootstrapping.value = false
      bootstrapped.value = true
    }
  }

  async function login(payload: LoginPayload): Promise<void> {
    const response = await authApi.login(payload)
    tokenStorage.setTokens(response.access_token, response.refresh_token)
    client.value = response.client
    bootstrapped.value = true
  }

  async function logout(): Promise<void> {
    try {
      await authApi.logout()
    } catch {
      // не критично, если уже разлогинены
    } finally {
      tokenStorage.clear()
      client.value = null
    }
  }

  /** Обновить часть данных клиента (после PATCH профиля). */
  function updateClient(updated: Partial<ClientProfile>): void {
    if (client.value) {
      client.value = { ...client.value, ...updated }
    }
  }

  return {
    client,
    bootstrapping,
    bootstrapped,
    isAuthenticated,
    bootstrap,
    login,
    logout,
    updateClient,
  }
})
